'use client';

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { PublicFullScreenLoader } from '@/components/navigation/PublicFullScreenLoader';

/** Rutas del hub público donde mostramos loader a pantalla completa */
function isPublicHubPath(path: string): boolean {
  return (
    path === '/' ||
    path === '/login' ||
    path.startsWith('/tournaments') ||
    path.startsWith('/activities')
  );
}

function isInternalPath(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

function isModifiedClick(event: MouseEvent): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

function getTournamentLoaderCopy(href: string | null) {
  const isDetail =
    href != null &&
    href.startsWith('/tournaments/') &&
    href !== '/tournaments' &&
    !href.endsWith('/admin');

  if (isDetail) {
    return {
      message: 'Cargando torneo...',
      subtitle: 'Preparando bracket e inscripciones',
    };
  }

  if (href === '/tournaments' || href?.startsWith('/tournaments')) {
    return {
      message: 'Cargando torneos...',
      subtitle: 'Un momento',
    };
  }

  return {
    message: 'Cargando...',
    subtitle: 'Un momento',
  };
}

/**
 * Muestra el loader a pantalla completa al instante en navegación del hub público
 * (complementa app/(public)/loading.tsx que puede tardar un frame en aparecer).
 */
export function PublicNavigationOverlay() {
  const location = useLocation();
  const pathname = location.pathname;
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  const [prevPathname, setPrevPathname] = useState(pathname);

  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setPendingHref(null);
  }

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!isPublicHubPath(pathname) || isModifiedClick(event)) return;

      const anchor = (event.target as HTMLElement).closest('a[href]');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || !isInternalPath(href) || href === pathname || href.startsWith('#')) return;

      setPendingHref(href);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [pathname]);

  if (!pendingHref) return null;

  const copy = getTournamentLoaderCopy(pendingHref);
  return <PublicFullScreenLoader message={copy.message} subtitle={copy.subtitle} />;
}
